(ns manage.ad-variant-preview.comments
  (:require ["@mui/material/Avatar$default" :as Avatar]
            ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Divider$default" :as Divider]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["moment/min/moment-with-locales$default" :as moment]
            ["src/svg/arrows/arrow-right.svg$default" :as ArrowRight]
            ["src/svg/artwork/cards.svg$default" :as CardsArtwork]
            ["src/svg/artwork/table.svg$default" :as TableArtwork]
            ["src/svg/inform/check-circle-fill.svg$default" :as CheckCircleFill]
            ["src/svg/inform/check-circle.svg$default" :as CheckCircle]
            [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.ocp.view :refer [defocp ocp]]
            [common.patterns.empty-state-view :refer [empty-state-view]]
            [common.queries :refer [use-infinite-query use-mutation use-query]]
            [common.resource.query.user :refer [user-query users-query]]
            [common.uix.hooks :refer [use-has-overflow]]
            [common.util :as util :refer [spread-obj]]
            [common.widgets.navigation-bar :refer [user-name->initials]]
            [common.widgets.video-timeline :refer [seconds-to-str]]
            [manage.ad-variant-preview.db :as db]
            [manage.ad-variant-preview.viewport :as viewport]
            [manage.ad-variant-preview.widgets.mentions :as mentions]
            [manage.ad-variant.queries :as q]
            [manage.ad-variant.widgets :as variant-widgets]
            [manage.slate-editor.mentions-node :as mentions-node]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defn- history-query-select [^js data]
  (->> (->clj data)
       (filter (fn [item] (#{"user-assignment" "status-change"} (:event-type item))))
       reverse))

(defn- comments-query-select [^js data]
  (->> (->clj data)
       (remove :comment/deleted)))

(defui new-comment [{:keys [id->user]}]
  (let [;; props is the comment top level fields to add-comment
        ;; info is extra data to help FE show the current new comment context
        {:keys [info props value]} (use-subscribe [::db/new-comment])
        {:keys [reply-to-user ad-variant-label]} info
        {:keys [timestamp]} (:comment/meta props)

        editing-id (:comment/id props)

        {:keys [adset-id agency-id advertiser-id campaign-id]} (use-subscribe [:url-context])
        visible-selection (use-subscribe [::db/visible])

        {review :data} (use-query (q/review-query {:adset-id adset-id}))
        allowed-ids (uix/use-memo (fn []
                                    (into #{(:request/created-by review)}
                                          (comp
                                           (filter #(= (:user-request-mapping/user-status %) "active"))
                                           (map :user-request-mapping/user-id))
                                          (:user-mappings review)))
                                  [review])

        {save-comment :mutate save-in-progress :isPending}
        (use-mutation (if editing-id
                        (q/edit-comment-mutation {:request-id (:request/id review)})
                        (q/add-comment-mutation {:request-id (:request/id review)
                                                 :agency-id agency-id
                                                 :advertiser-id advertiser-id
                                                 :campaign-id campaign-id
                                                 :adset-id adset-id
                                                 :selected-ad-variants visible-selection})))

        mentions-ref (uix/use-ref)]

    (uix/use-effect (fn []
                      ;; Focus the comment input when user starts editing or replying.
                      (when (or editing-id reply-to-user)
                        (some-> @mentions-ref .focus)))
                    [editing-id reply-to-user])

    ($ Stack
       {:component "form"
        :direction "column"
        :onSubmit (fn [e]
                    (.preventDefault e)
                    (save-comment {:value (str/trim value)
                                   :props props}
                                  #js {:onSuccess (fn [_]
                                                    (rf/dispatch [::db/cancel-new-comment]))}))
        :sx #js {:gap 1}}
       ($ Stack
          {:direction "column"
           :gap 0.5
           :sx #js {:flexGrow 1
                    :overflow "hidden"}}
          (when ad-variant-label
            ($ Tooltip
               {:placement "top"
                :title ad-variant-label}
               ($ Chip
                  {:sx #js {:minWidth 0
                            :fontSize "sm"
                            :justifyContent "flex-start"
                            :height "30px"}
                   :label ($ :span ad-variant-label
                             (when timestamp
                               ($ :<>
                                  ($ :span " - ")
                                  ($ Typography
                                     {:component "span"
                                      :fontSize "inherit"
                                      :color "primary"}
                                     (seconds-to-str timestamp)))))})))

          (when reply-to-user
            ($ Chip
               {:sx #js {:minWidth 0
                         :fontSize "sm"
                         :justifyContent "flex-start"
                         :height "30px"}
                :label ($ :span "Replying to "
                          ($ Typography
                             {:component "span"
                              :fontSize "inherit"
                              :color "primary"}
                             (:name (get id->user reply-to-user))))}))

          (when editing-id
            ($ Chip
               {:sx #js {:minWidth 0
                         :fontSize "sm"
                         :justifyContent "flex-start"
                         :height "30px"}
                :label "Editing"}))

          ($ mentions/mentions
             {:ref mentions-ref
              :placeholder "Add comment..."
              :allowed-ids allowed-ids
              :initial-value value
              :on-change #(rf/dispatch [::db/set-new-comment-value %])
              :sx #js {:minHeight "112px !important"}}))

       ($ Stack
          {:direction "row"
           :gap 1
           :justifyContent "flex-end"}
          ($ Button
             {:sx #js {:color "text.primary"}
              :onClick (fn [_e]
                         (rf/dispatch [::db/cancel-new-comment]))}
             "Cancel")
          ($ Button
             {:variant "contained"
              :color "primary"
              :disabled (or (empty? value)
                            save-in-progress)
              :type "submit"}
             (if editing-id
               "Save"
               "Comment"))))))

(defui avatar [{:keys [user]}]
  ($ Avatar
     {:sx #js {:backgroundColor "primary.light"
               :fontSize "h3.fontSize"
               :width 32
               :height 32}}
     (user-name->initials (:name user))))

(defui timestamp [{:keys [time]}]
  ($ Tooltip
     {:placement "top"
      :title (util/format-iso-string time)}
     ($ Typography
        {:component "span"
         :sx #js {:color "grey.30"}}
        (.fromNow (moment time)))))

(defui item-header [{:keys [children user time]}]
  ($ Stack
     {:direction "row"
      :justifyContent "space-between"
      :alignItems "center"}
     ($ Stack
        {:direction "row"
         :alignItems "center"
         :gap 1}
        ($ avatar {:user user})
        ($ Typography
           {:component "span"
            :sx #js {:fontWeight 600}}
           (:name user))
        ($ timestamp {:time time}))
     children))

(defui comment-resolved-toggle [{:keys [comment]}]
  (let [{set-comment-resolution :mutate} (use-mutation (q/set-comment-resolution-mutation {:request-id (:comment/request-id comment)}))]
    ($ Tooltip {:placement "left"
                :title (if (:comment/resolved comment)
                         "Comment resolved"
                         "Mark as resolved")}
       ($ IconButton
          {:color (if (:comment/resolved comment)
                    "success"
                    "grey.20")
           :disableRipple (:comment/resolved comment)
           ;; FIXME: The click area is a bit small, but the look the design spacing
           :sx #js {:p 0}
           :onClick (fn [_e]
                      (set-comment-resolution {:comment-id (:comment/id comment)
                                               :resolved (not (:comment/resolved comment))}))}
          (if (:comment/resolved comment)
            ($ CheckCircleFill)
            ($ CheckCircle))))))

(defui comment-message [{:keys [message]}]
  (let [;; Show only 5 lines by default, more will overflow.
        ;; If there is overflow, show a button and show-all toggle will disable max-height.
        [show-all set-show-all] (uix/use-state false)
        [message-measure-ref has-overflow] (use-has-overflow)]
    ($ :<>
       ($ Box {:ref message-measure-ref
               :sx #js {:whiteSpace "pre-wrap"
                        :lineHeight "1.5"
                        ;; 5 lines, 1.5 times line height
                        :maxHeight (when-not show-all "7.5em")
                        :overflow "hidden"}}
          ;; `message-preview` component uses initial-value. So to make it reactive to the value changes
          ;; We need to trigger a re-render using key when the value changes
          ($ mentions/message-preview {:value message
                                       :key message}))
       (when (and has-overflow
                  (not show-all))
         ($ Button
            {:variant "text"
             :size "small"
             :onClick (fn [_e] (set-show-all true))}
            "Show all")))))

(defui subject-info [{:keys [subject-type subject-id comment-meta ad-variant-uuid-idx]}]
  (let [{:keys [event-target item-places-ref] :as ctx} (viewport/use-context)
        ;; NOTE: It seems heavy to have to pass in all the ad-variants the whole way from ad-variant-page,
        ;; re-frame subs would be nice here.
        subject-ad-variant-label (when (= "ad-variant" subject-type)
                                   (or (get-in ad-variant-uuid-idx [subject-id :content-id])
                                       "Unknown ad variant"))]
    (when subject-ad-variant-label
      ($ Stack
         {:direction "row"
          :alignItems "center"
          :gap 0.5
          :onClick (fn [_e]
                     (when-let [x (get (.-current item-places-ref) subject-id)]
                       (viewport/center-on-coords ctx x)
                       (when-let [ts (:timestamp comment-meta)]
                         (viewport/dispatch event-target "seek" {:target ts}))))
          :sx (spread-obj #js {:overflow "hidden"}
                          (when (and item-places-ref (get (.-current item-places-ref) subject-id))
                            #js {:cursor "pointer"}))}
         ($ Typography
            {:sx #js {:color "grey.20"
                      :whiteSpace "nowrap"
                      :fontWeight 500}}
            "Ad variant:")
         ($ Tooltip
            {:placement "top"
             :title subject-ad-variant-label}
            ($ Chip
               {:sx #js {:minWidth 0}
                :label ($ :span subject-ad-variant-label
                          (when-let [ts (:timestamp comment-meta)]
                            ($ :<>
                               ($ :span " - ")
                               ($ Typography
                                  {:component "span"
                                   :fontSize "inherit"
                                   :color "primary"}
                                  (seconds-to-str ts)))))}))))))

(defui activity-box [{:keys [user time header-actions children]}]
  ($ Paper
     {:component Stack
      :gap 1.5
      :sx #js {:backgroundColor "grey.80"
               :pt 1
               :p 1.5}}
     ($ item-header
        {:user user
         :time time}
        header-actions)
     children))

(defui comment-content [{:keys [is-reply comment replies id->user ad-variant-uuid-idx show-tools?]}]
  (let [{{user-id :id} :data} (use-query (user-query))
        is-own (= user-id (:comment/created-by comment))
        has-replies (seq replies)

        {delete-comment :mutate} (use-mutation (q/delete-comment-mutation {:request-id (:comment/request-id comment)}))

        tools (->> [{:key :reply
                     :label "Reply"
                     :enabled (not is-reply)
                     :onClick (fn [_e]
                                (rf/dispatch [::db/set-new-comment-info {:value ""
                                                                         :props {:comment/parent-id (:comment/id comment)}
                                                                         :info {:reply-to-user (:comment/created-by comment)}}]))}
                    {:key :edit
                     :label "Edit"
                     :enabled is-own
                     :onClick (fn [_e]
                                (rf/dispatch [::db/set-new-comment-info {:value (:comment/message comment)
                                                                         :props {:request/id (:comment/request-id comment)
                                                                                 :comment/id (:comment/id comment)}}]))}
                    {:key :delete
                     :label "Delete"
                     :enabled (and is-own (not has-replies))
                     :onClick (fn [_e]
                                (delete-comment {:comment-id (:comment/id comment)}))}]
                   (filter :enabled))]
    ($ :<>
       ($ subject-info
          {:subject-type (:comment/subject-type comment)
           :subject-id (:comment/subject-id comment)
           :ad-variant-uuid-idx ad-variant-uuid-idx
           :comment-meta (:comment/meta comment)})

       ($ comment-message
          {:message (:comment/message comment)})

       (when show-tools?
         ($ Stack
            {:direction "row"
             :alignItems "center"
             :sx #js {".MuiButton-root" #js {:minWidth "auto"}}}
            (for [tool tools]
              ($ :<>
                 {:key (:key tool)}
                 ($ Button
                    {:variant "text"
                     :color "primary"
                     :onClick (:onClick tool)}
                    (:label tool))))))

       (when has-replies
         ($ :<>
            ;; "View y more replies" (if more than 1?)
            ;; Just show 1 last by default and click on the previous item to show all?
            (for [{:keys [comment/id] :as comment} replies]
              (let [user (or (get id->user (:comment/created-by comment))
                             {:name "Unknown user"})]
                ($ Stack
                   {:key id
                    :gap 1.5}
                   ($ Divider {:sx #js {:mx -1.5 :borderColor "grey.70"}})
                   ($ item-header
                      {:user user
                       :time (:comment/created-at comment)})
                   ($ comment-content
                      {:is-reply true
                       :comment comment
                       :id->user id->user
                       :show-tools? show-tools?})))))))))

(defui comment-box [{:keys [is-reply comment replies id->user ad-variant-uuid-idx show-tools?]}]
  (let [user (or (get id->user (:comment/created-by comment))
                 {:name "Unknown user"})
        time (:comment/created-at comment)]
    ($ activity-box
       {:user user
        :time time
        :header-actions (when (and (not is-reply) show-tools?) ; TODO: CLIENT
                          ($ comment-resolved-toggle {:comment comment}))}
       ($ comment-content
          {:is-reply is-reply
           :comment comment
           :id->user id->user
           :replies replies
           :ad-variant-uuid-idx ad-variant-uuid-idx
           :show-tools? show-tools?}))))

(defui history-status-change [{:keys [previous-status status]}]
  ($ Stack
     {:direction "row"
      :alignItems "center"
      :gap 0.5}
     ($ Typography
        {:sx #js {:color "grey.20"
                  :fontWeight 500}}
        "Status:")
     (when previous-status
       ($ :<>
          ($ variant-widgets/status-chip
             {:status previous-status
              :review? true})
          ($ SvgIcon {:component ArrowRight})))
     ($ variant-widgets/status-chip
        {:status status
         :review? true})))

(defui history-status-event-display [{:keys [ad-variant-uuid-idx item]}]
  (let [events-by-status (uix/use-memo
                          (fn []
                            (->> (reduce (fn [acc {:history/keys [event-payload subject-type subject-id] :as event}]
                                           (let [ad-variant (when (= subject-type "ad-variant")
                                                              (get ad-variant-uuid-idx subject-id))
                                                 status (:status event-payload)
                                                 previous-status (if (nil? (:previous-status event-payload))
                                                                   (:approval ad-variant)
                                                                   (:previous-status event-payload))
                                                 ;; key is a composite value made up of status and previous status
                                                 key (cond-> {:status status}
                                                       (not= status previous-status) (assoc :previous-status previous-status)
                                                       :else identity)]
                                             (update acc key conj event)))
                                         {} (:aggregated-events item))))
                          [item ad-variant-uuid-idx])]
    (for [[{:keys [previous-status status]} events] events-by-status]
      ($ Stack {:direction "column"
                :gap 1
                :key (str previous-status status)}
         (for [{:history/keys [subject-id]} events]
           ($ subject-info
              {:key subject-id
               :subject-id subject-id
               :subject-type "ad-variant"
               :comment-meta nil
               :ad-variant-uuid-idx ad-variant-uuid-idx}))
         (when status
           ($ history-status-change {:previous-status previous-status :status status}))))))

(defui history-user-assignement-display [{:keys [id->user item]}]
  (let [items (->> item
                   :aggregated-events
                   (map #(:history/event-payload %)))
        users (for [{:keys [user-id]} items]
                (let [{:keys [name]} (get id->user user-id)]
                  name))
        users-concatenated (if (> (count users) 1)
                             (str (str/join ", " (butlast users)) " and " (last users))
                             users)]
    ($ Stack
       {:direction "column"
        :gap 1
        :sx #js {:flexGrow 1
                 :overflow "hidden"
                 :whiteSpace "pre-wrap"}}
       ($ Typography {:sx #js {:color "grey.20"}}
          ($ mentions-node/mention users-concatenated) (if (> (count users) 1) " were" " was") " assigned to this review")
       (for [{:keys [user-id message]} items]
         (let [{:keys [name]} (get id->user user-id)]
           (when message
             ($ Typography {:sx #js {:color "grey.20"}
                            :key user-id}
                ($ mentions-node/mention name) (str " was sent a message: " message))))))))

(defui history-box [{:keys [item id->user ad-variant-uuid-idx]}]
  (let [{:keys [event-type]} item
        user (or (get id->user (:history/created-by item))
                 {:name "Unknown user"})]
    ($ activity-box
       {:user user
        :time (:created-at item)}
       (case event-type
         "status-change" ($ history-status-event-display {:ad-variant-uuid-idx ad-variant-uuid-idx
                                                          :item item})
         "user-assignment" ($ history-user-assignement-display {:id->user id->user
                                                                :item item})
         ""))))

(defui activity-log [{:keys [review ad-variants comment-mode? ad-variant-uuid]}]
  (let [{:keys [agency-id]} (use-subscribe [:url-context])
        {comments :data} (use-query (spread-obj (q/comments-query {:request-id (:request/id review)})
                                                #js {:select comments-query-select}))

        {history :data} (use-query (spread-obj (q/history-query {:request-id (:request/id review)})
                                               #js {:select history-query-select}))

        {:keys [data isPending]} (use-query (users-query {:agency-id agency-id}))
        no-users? (not (seq data))
        id->user (uix/use-memo (fn [] (util/index-by :id data)) [data])

        top-level-comments (uix/use-memo (fn [] (reverse (remove :comment/parent-id comments))) [comments])

        ;; In comment mode, display only comments related to the selected ad variant.
        ;; Otherwise, display all comments and history items.
        ;; In both cases, sort the items by creation time in descending order.
        items (uix/use-memo (fn []
                              (if comment-mode?
                                (->> top-level-comments
                                     (filter #(= (:comment/subject-id %) ad-variant-uuid))
                                     (sort-by :comment/created-at >))

                                (->> (concat top-level-comments history)
                                     (sort-by (fn [x]
                                                (or (:comment/created-at x)
                                                    (:created-at x)))
                                              >))))
                            [comment-mode? ad-variant-uuid top-level-comments history])

        replies (uix/use-memo (fn []
                                (->> comments
                                     (filter :comment/parent-id)
                                     (group-by :comment/parent-id)))
                              [comments])

        uuid->ad-variant (->> (map :data ad-variants)
                              (mc/index-by :uuid))

        no-user-access-warning-shown (use-subscribe [::db/no-user-access-warning-shown])]

    (uix/use-effect
     (fn []
       (when (and (not isPending) no-users? (not no-user-access-warning-shown))
         (rf/dispatch [::db/no-user-access-warning-shown]) ; set to ensure modal is shown only once
         (rf/dispatch [:modal/open {:id :ad-variant/no-user-access}])))
     [isPending no-users? no-user-access-warning-shown])

    ($ Stack
       {:direction "column"
        :gap 2
        :sx #js {:flex 1}}
       ($ Stack
          {:gap 2
           :sx #js {:flex 1
                    :overflowY "auto"
                    :overflowX "hidden"
                    :position "relative"}}
          (cond
            (seq items) (for [item items]
                          (if-let [id (:comment/id item)]
                            ($ comment-box
                               {:key (str "comment-" id)
                                :comment item
                                :replies (get replies id)
                                ;; FIXME: Rather not passing this all the way down
                                :id->user id->user
                                :ad-variant-uuid-idx uuid->ad-variant
                                :show-tools? comment-mode?})
                            ($ history-box
                               {:key (str "history-" (:id item))
                                :item item
                                :id->user id->user
                                :ad-variant-uuid-idx uuid->ad-variant})))
            :else ($ empty-state-view
                     (if comment-mode?
                       {:artwork CardsArtwork
                        :title "No comments yet"
                        :description "Be the first to share your thoughts"}
                       {:artwork TableArtwork
                        :title "No activity yet"
                        :description "Activity will appear here as things happen"}))))
       (when comment-mode?
         ($ new-comment
            {:id->user id->user})))))

(defocp comments-ocp :comments/ocp
  {:width "440px"
   :disable-form true}
  [{:keys [comment-mode? ad-variant-uuid]}]
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {{:keys [pages]} :data} (use-infinite-query (q/ad-variants-query {:advertiser-id advertiser-id
                                                                          :adset-id adset-id}))
        ad-variants (uix/use-memo (fn [] (mapcat :data pages)) [pages])
        {review :data} (use-query (q/review-query {:adset-id adset-id}))]
    ($ ocp
       {:title (if comment-mode? "Comments" "Activity log")}
       ($ activity-log
          {:review review
           :ad-variants ad-variants
           :comment-mode? comment-mode?
           :ad-variant-uuid ad-variant-uuid}))))
